<template>
  <Modal :modal-active="isOpen" @close="closeModal" max-width="max-w-4xl" max-height="65vh">
    <template #header>
      <h3 class="text-lg text-textPrimary text-center w-full">Filtros</h3>
    </template>

    <div class="flex flex-grow overflow-hidden p-0 m-0 -mx-6 -my-4">
      <div class="w-1/4 bg-gray-50 border-r border-gray-200 flex-shrink-0">
        <nav>
          <a
            v-for="section in sections"
            :key="section.id"
            href="#"
            @click.prevent="scrollToSection(section.id)"
            :class="[
              'flex items-center p-4 text-sm font-medium transition-colors',
              activeSection === section.id
                ? 'bg-primary30 text-white'
                : 'text-textPrimary hover:bg-gray-200'
            ]"
          >
            <component
              :is="section.icon"
              class="h-5 w-5 mr-3"
              :color="activeSection === section.id ? 'white' : 'black'"
            />
            <span :class="activeSection === section.id && 'font-bold'">{{ section.title }}</span>
          </a>
        </nav>
      </div>

      <div ref="contentArea" @scroll="handleScroll" class="flex-grow p-6 overflow-y-auto">
        <div :id="sections[0].id" class="mb-10">
          <h4 class="font-bold text-gray-800 mb-6 text-left">{{ sections[0].title }}</h4>
          <div class="grid grid-cols-1 gap-6">
            <v-autocomplete
              label="Empresa"
              :items="listCompanies"
              v-model="localFilters.companyIds"
              multiple
              chips
              closable-chips
              variant="outlined"
              density="compact"
              class="multi-chip-autocomplete"
              hide-details
            ></v-autocomplete>
            <v-autocomplete
              label="Unidades"
              :items="healthUnitOptions"
              v-model="localFilters.healthUnitIds"
              multiple
              chips
              closable-chips
              variant="outlined"
              density="compact"
              class="multi-chip-autocomplete"
              hide-details
            ></v-autocomplete>
            <!-- <v-autocomplete
              label="Escalas"
              :items="listSchedules"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details
            ></v-autocomplete> -->
            <v-autocomplete
              label="Posições"
              :items="positionsOptions"
              v-model="localFilters.positionIds"
              multiple
              chips
              closable-chips
              variant="outlined"
              density="compact"
              class="multi-chip-autocomplete"
              hide-details
            ></v-autocomplete>
            <v-autocomplete
              label="Grupos Operacionais"
              :items="operationalGroupOptions"
              v-model="localFilters.operationalGroups"
              multiple
              chips
              closable-chips
              clearable
              variant="outlined"
              density="compact"
              class="multi-chip-autocomplete"
              hide-details
            ></v-autocomplete>
            <!-- <v-autocomplete
              label="Turnos"
              :items="shiftsOptions"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details
            ></v-autocomplete> -->
          </div>
        </div>

        <div :id="sections[1].id" class="mb-10">
          <h4 class="font-bold text-gray-800 mb-6 text-left">{{ sections[1].title }}</h4>
          <div class="grid grid-cols-1 gap-6">
            <!-- Busca no servidor (web#3352): `no-filter` porque o servidor já filtrou por
              nome e CRM, e o Vuetify refiltraria só pelo título. -->
            <v-autocomplete
              label="Médico"
              :items="doctorOptions"
              v-model="localFilters.doctorIds"
              :loading="isSearchingDoctors"
              :no-data-text="doctorNoDataText"
              no-filter
              item-props
              multiple
              chips
              closable-chips
              variant="outlined"
              density="compact"
              class="multi-chip-autocomplete"
              hide-details
              hint="Funcionalidade em desenvolvimento"
              persistent-hint
              data-testid="reports-filter-doctor"
              @update:search="searchDoctors"
            >
              <template v-if="doctorOverflowText" #append-item>
                <v-list-item :title="doctorOverflowText" disabled />
              </template>
            </v-autocomplete>
            <v-autocomplete
              label="Excepcionalidade"
              :items="[
                { title: 'Com excepcionalidade', value: true },
                { title: 'Sem excepcionalidade', value: false }
              ]"
              v-model="localFilters.hasException"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              data-testid="reports-filter-has-exception"
            ></v-autocomplete>
            <!-- <v-autocomplete
              label="Alocador"
              :items="listAdminUsers"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details 
            ></v-autocomplete> -->
          </div>
        </div>

        <div :id="sections[2].id" class="mb-10">
          <h4 class="font-bold text-gray-800 mb-6 text-left">{{ sections[2].title }}</h4>
          <div class="grid grid-cols-2 gap-6">
            <DatePicker v-model="localFilters.startDate">
              <template #default="{ inputEvents }">
                <v-text-field
                  :model-value="formattedStartDate"
                  label="Dia inicial"
                  :append-inner-icon="mdiCalendarBlankOutline"
                  v-on="inputEvents"
                  readonly
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </template>
            </DatePicker>
            <DatePicker v-model="localFilters.endDate">
              <template #default="{ inputEvents }">
                <v-text-field
                  :model-value="formattedEndDate"
                  label="Dia final"
                  :append-inner-icon="mdiCalendarBlankOutline"
                  v-on="inputEvents"
                  readonly
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </template>
            </DatePicker>
            <!-- <v-autocomplete
              label="Dias da semana"
              :items="['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo']"
              variant="outlined"
              density="compact"
              hide-details
              class="col-span-2 h-12"
            ></v-autocomplete> -->
            <!-- <v-autocomplete
              label="Registro de plantão"
              :items="['Registrado', 'Não Registrado']"
              variant="outlined"
              density="compact"
              hide-details
              class="col-span-2 h-12"
            ></v-autocomplete> -->
          </div>
        </div>

        <!-- <div :id="sections[3].id">
          <h4 class="font-bold text-gray-800 mb-6 text-left">{{ sections[3].title }}</h4>
          <div class="grid grid-cols-2 gap-6">
            <v-text-field
              label="Valor mínimo"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details
            ></v-text-field>
            <v-text-field
              label="Valor máximo"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details
            ></v-text-field>
            <v-autocomplete
              label="Antecipação solicitada"
              :items="['Sim', 'Não']"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details
            ></v-autocomplete>
            <v-autocomplete
              label="Antecipação paga"
              :items="['Sim', 'Não']"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details
            ></v-autocomplete>
            <v-autocomplete
              label="Modalidade de repasse"
              :items="[
                { title: 'Fluxo', value: 'FLOW' },
                { title: 'À vista', value: 'IMMEDIATE' }
              ]"
              v-model="localFilters.paymentTiming"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details
              clearable
            ></v-autocomplete>
            <v-autocomplete
              label="Equipe"
              :items="['Interno', 'Externo']"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details
            ></v-autocomplete>
            <v-autocomplete
              label="Excepcionalidade"
              :items="['Sim', 'Não']"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details
            ></v-autocomplete>
            <v-autocomplete
              label="Status do plantão"
              :items="['Aberto', 'Fechado']"
              variant="outlined"
              density="compact"
              class="h-12"
              hide-details
            ></v-autocomplete>
            <v-autocomplete
              label="Possui Check-in"
              :items="[
                { title: 'Sim', value: true },
                { title: 'Não', value: false }
              ]"
              v-model="localFilters.isChecked"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            ></v-autocomplete>
            <v-autocomplete
              label="Periodicidade"
              :items="['Diário', 'Semanal']"
              variant="outlined"
              density="compact"
              hide-details
              class="col-span-2 h-12"
            ></v-autocomplete>
          </div>
        </div> -->

        <div class="flex justify-between w-full gap-x-3 mt-12">
          <OutlinedButton width="130px" text="Limpar filtros" @click="clearFilters" height="48px" />
          <FilledButton
            width="150px"
            text="Aplicar filtros"
            @click="applyFilters"
            height="48px"
            class="!bg-primary30"
          />
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'
import { mdiCalendarBlankOutline } from '@mdi/js'
import { storeToRefs } from 'pinia'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import Network from '@/Network'
import { useOperationalGroups } from '@/hooks/useOperationalGroups'
import { format, parseISO } from 'date-fns'
import ScheduleService from '@/Network/Services/ScheduleUnitService'
import { useErrorToast } from '@/composables/useErrorToast'
import { useDoctorFilterSearch } from '../composables/useDoctorFilterSearch'

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  activeFilters: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['update:isOpen', 'apply-filters'])

const getInitialFilters = () => JSON.parse(JSON.stringify(props.activeFilters))

const localFilters = ref(getInitialFilters())

// Estado próprio do modal: não lê nem grava o `useDoctorStore`, que outras telas sujam.
const {
  options: doctorOptions,
  isSearching: isSearchingDoctors,
  noDataText: doctorNoDataText,
  overflowText: doctorOverflowText,
  search: searchDoctors,
  reset: resetDoctorSearch
} = useDoctorFilterSearch(computed(() => localFilters.value.doctorIds))

const positionsData = ref<any[]>([])
const positionsOptions = computed(() =>
  positionsData.value.map((pos) => ({
    title: pos.name,
    value: pos.id
  }))
)

const { options: operationalGroupOpts, load: loadOperationalGroups } = useOperationalGroups()
const operationalGroupOptions = computed(() =>
  operationalGroupOpts.value.map((opt) => ({
    title: opt.label,
    value: opt.value
  }))
)

const { notifyError } = useErrorToast()

// O select "Turnos" está comentado no template: ao reativá-lo, recriar `shiftData`
// e carregar `Network.shift.get()` com try/catch e `notifyError` próprios — num
// Promise.all junto das posições, a falha dos turnos esvaziaria as posições.
// const shiftsOptions = computed(() =>
//   shiftData.value.map((pos) => ({
//     title: pos.name,
//     value: pos.id
//   }))
// )

const loadFilterOptions = async () => {
  try {
    positionsData.value = await Network.position.get()
  } catch (error) {
    // Sem o aviso, o select "Posições" abre vazio e parece que não há posições.
    console.error('Erro ao carregar as posições do filtro:', error)
    notifyError(error, 'Não foi possível carregar as posições para o filtro.')
  }
}

const healthUnitStore = useHealthUnitStore()
const scheduleStore = useScheduleStore()

watch(
  () => props.isOpen,
  (isOpening) => {
    if (isOpening) {
      localFilters.value = getInitialFilters()
      healthUnitStore.fetchAllHealthUnitsForSelect()
      scheduleStore.getAllSchedules()
      resetDoctorSearch()
      loadFilterOptions()
      loadOperationalGroups()
    }
  }
)

const formattedStartDate = computed(() => {
  if (!localFilters.value.startDate) return ''
  const date =
    localFilters.value.startDate instanceof Date
      ? localFilters.value.startDate
      : parseISO(localFilters.value.startDate)
  return format(date, 'dd/MM/yyyy')
})

const formattedEndDate = computed(() => {
  if (!localFilters.value.endDate) return ''
  const date =
    localFilters.value.endDate instanceof Date
      ? localFilters.value.endDate
      : parseISO(localFilters.value.endDate)
  return format(date, 'dd/MM/yyyy')
})

const ScaleIcon = defineAsyncComponent(() => import(`@/assets/icons/scale.vue`))
const ShiftIcon = defineAsyncComponent(() => import(`@/assets/icons/hospital.vue`))
const WorkJourneyIcon = defineAsyncComponent(() => import(`@/assets/icons/clock.vue`))
// const PaymentIcon = defineAsyncComponent(() => import(`@/assets/icons/money.vue`))

const { listCompanies } = storeToRefs(useCompanyStore())
// const { listSchedules } = storeToRefs(scheduleStore)
const { listHealthUnits } = storeToRefs(healthUnitStore)

const listAdminUsers = ref<{ id: number; name: string }[]>([])
const scheduleService = new ScheduleService()

// Se o backend ainda não expõe companyId (deploy do front antes do da API,
// bundle em cache, rollback), a cascata fica inerte e o select mostra todas as
// unidades — falha para o lado seguro, em vez de ficar vazio.
const hasCompanyMetadata = computed(() =>
  listHealthUnits.value.some((unit) => unit.companyId !== undefined)
)

const healthUnitOptions = computed(() => {
  const selectedCompanyIds = localFilters.value.companyIds ?? []
  const canCascade = selectedCompanyIds.length > 0 && hasCompanyMetadata.value
  const units = canCascade
    ? listHealthUnits.value.filter((unit) => selectedCompanyIds.includes(unit.companyId))
    : listHealthUnits.value

  return units.map((unit) => ({
    title: unit.title,
    value: String(unit.value)
  }))
})

const fetchAdminUsers = async (scheduleId: number) => {
  try {
    listAdminUsers.value = await scheduleService.stationCreatingUsers(scheduleId)
  } catch (error) {
    // fallback intencional: o único consumidor de `listAdminUsers` é o select
    // "Alocador", que está comentado no template (funcionalidade em
    // desenvolvimento). Sem campo na tela, um toast avisaria sobre um filtro que
    // o usuário nem vê. Ao reativar o select, trocar por
    // `notifyError(error, 'Não foi possível carregar os responsáveis pela escala.')`
    // e estado visível no campo.
    console.error('Error fetching admin users:', error)
  }
}

watch(
  () => localFilters.value.companyIds,
  () => {
    const selectedCompanyIds = localFilters.value.companyIds ?? []
    if (selectedCompanyIds.length > 0) {
      localFilters.value.healthUnitIds = (localFilters.value.healthUnitIds ?? []).filter(
        (huId: string) => healthUnitOptions.value.some((opt) => opt.value === huId)
      )
    }
  }
)

watch(
  () => localFilters.value.scheduleId,
  (newScheduleId) => {
    if (newScheduleId) {
      fetchAdminUsers(newScheduleId)
    } else {
      listAdminUsers.value = []
    }
  },
  { immediate: true }
)

const sections = ref([
  { id: 'escala', title: 'Escala', icon: ScaleIcon },
  { id: 'plantao', title: 'Plantão', icon: ShiftIcon },
  { id: 'jornada', title: 'Jornada de trabalho', icon: WorkJourneyIcon }
  // { id: 'pagamento', title: 'Pagamento', icon: PaymentIcon }
])

const contentArea = ref<HTMLElement | null>(null)
const activeSection = ref('escala')

function closeModal() {
  emit('update:isOpen', false)
}

function applyFilters() {
  const filtersToApply = { ...localFilters.value }

  if (filtersToApply.startDate instanceof Date) {
    filtersToApply.startDate = filtersToApply.startDate.toISOString()
  }

  if (filtersToApply.endDate instanceof Date) {
    filtersToApply.endDate.setHours(23, 59, 59, 999)
    filtersToApply.endDate = filtersToApply.endDate.toISOString()
  }

  emit('apply-filters', filtersToApply)
  closeModal()
}

function clearFilters() {
  localFilters.value = {
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
    endDate: new Date().toISOString(),
    companyIds: [],
    healthUnitIds: [],
    doctorIds: [],
    positionIds: [],
    operationalGroups: [],
    isChecked: undefined,
    paymentTiming: undefined,
    hasException: undefined
  }
}

function scrollToSection(sectionId: string) {
  const sectionElement = document.getElementById(sectionId)
  if (sectionElement && contentArea.value) {
    contentArea.value.scrollTo({
      top: sectionElement.offsetTop - contentArea.value.offsetTop,
      behavior: 'smooth'
    })
  }
}

function handleScroll() {
  const scrollContainer = contentArea.value
  if (!scrollContainer) return

  const scrollPosition = scrollContainer.scrollTop
  const scrollOffset = 100
  let currentSectionId = sections.value[0].id

  for (const section of sections.value) {
    const sectionElement = document.getElementById(section.id) as HTMLElement
    if (
      sectionElement &&
      sectionElement.offsetTop - scrollContainer.offsetTop <= scrollPosition + scrollOffset
    ) {
      currentSectionId = section.id
    }
  }
  activeSection.value = currentSectionId
}
</script>

<style scoped>
.modal-pushed-by-sidebar {
  transform: translateX(-14rem);
  transition: transform 300ms ease;
}

.multi-chip-autocomplete :deep(.v-field__input) {
  max-height: 112px;
  overflow-y: auto;
  flex-wrap: wrap;
}
</style>
